import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { UsersThree, Crown, Heart, Eye } from '@phosphor-icons/react';
import { api } from '@/App';

const ROLE_LABELS = {
  spouse: { label: 'Spouse / Partner', icon: Heart, color: '#C53030' },
  advisor: { label: 'Read-Only Advisor', icon: Eye, color: '#4A5568' },
};

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/household/invitation/${token}`)
      .then(response => {
        setInvitation(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invitation not found');
        setLoading(false);
      });
  }, [token]);

  const handleAccept = async () => {
    if (!localStorage.getItem('auth_token')) {
      toast.info('Please sign in with the email this was sent to');
      navigate('/auth');
      return;
    }
    setAccepting(true);
    try {
      await api.post(`/household/accept/${token}`);
      toast.success('You\'ve joined the household!');
      navigate('/household');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB] text-[#4A5568]">Loading invitation...</div>;
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB]">
        <div className="text-center">
          <h1 className="cabinet-grotesk text-2xl text-[#0A1128] mb-2">Invitation Unavailable</h1>
          <p className="text-[#4A5568] mb-4">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-[#0A1128] hover:bg-[#152145]">Go to PLANIVANCE AI</Button>
        </div>
      </div>
    );
  }

  const RoleConfig = ROLE_LABELS[invitation.role] || ROLE_LABELS.spouse;
  const Icon = RoleConfig.icon;

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8">
          <div className="text-center mb-6">
            <div className="inline-block p-4 bg-[#C5A059]/10 rounded-full mb-4">
              <UsersThree size={40} weight="duotone" className="text-[#C5A059]" />
            </div>
            <h1 className="cabinet-grotesk text-2xl font-medium text-[#0A1128] mb-2">You're Invited!</h1>
            <p className="text-sm text-[#4A5568]">
              <strong>{invitation.invited_by}</strong> has invited you to join
            </p>
            <p className="cabinet-grotesk text-xl font-medium text-[#C5A059] mt-1">{invitation.household_name}</p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-md" style={{ backgroundColor: `${RoleConfig.color}15` }}>
                <Icon size={20} weight="duotone" style={{ color: RoleConfig.color }} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Your Role</p>
                <p className="font-medium text-[#1A202C]">{RoleConfig.label}</p>
              </div>
            </div>
            <p className="text-xs text-[#4A5568]">
              Invited email: <strong>{invitation.invitee_email}</strong>
            </p>
          </div>

          <Button onClick={handleAccept} disabled={accepting} data-testid="accept-invitation-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
            {accepting ? 'Joining...' : 'Accept Invitation'}
          </Button>

          <p className="text-xs text-[#4A5568] text-center mt-4">
            You must sign in with <strong>{invitation.invitee_email}</strong> to accept.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
